import { useEffect, useRef, useState } from 'react';
import type { FormIOSchema } from '@formio/react';
import { onAnnouncement } from './ehs';

type FormioSchemaComponent = {
  conditional?: unknown;
  customConditional?: unknown;
  logic?: unknown[];
  components?: FormioSchemaComponent[];
  columns?: Array<{ components?: FormioSchemaComponent[] }>;
  rows?: Array<Array<{ components?: FormioSchemaComponent[] }>>;
};

/**
 * Subscribes to Formio visibility announcements and exposes the current text
 * for a single status live region.
 */
export function useFormioAnnouncements(): string {
  const [announcement, setAnnouncement] = useState('');
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    const unsubscribe = onAnnouncement(message => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      setAnnouncement(message);
      timerRef.current = setTimeout(() => setAnnouncement(''), 1000);
    });

    return () => {
      unsubscribe();
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  return announcement;
}

/**
 * Checks whether a Formio schema can reveal/hide questions dynamically.
 */
export function hasConditionalQuestions(schema: FormIOSchema): boolean {
  return containsConditionalComponent(
    (schema as { components?: FormioSchemaComponent[] }).components ?? []
  );
}

function containsConditionalComponent(
  components: FormioSchemaComponent[]
): boolean {
  return components.some(component => {
    if (
      component.conditional ||
      component.customConditional ||
      (Array.isArray(component.logic) && component.logic.length > 0)
    ) {
      return true;
    }

    return containsConditionalComponent(getChildComponents(component));
  });
}

function getChildComponents(
  component: FormioSchemaComponent
): FormioSchemaComponent[] {
  return [
    ...(component.components ?? []),
    ...(component.columns ?? []).flatMap(column => column.components ?? []),
    ...(component.rows ?? []).flatMap(row =>
      row.flatMap(cell => cell.components ?? [])
    )
  ];
}
